/**
 * useLoungeSession - Centralized Lounge Session Management
 * 
 * Purpose: Single source of truth for entering/leaving the Lifestyle Lounge
 * Ensures comprehensive cleanup on exit, preventing ghost presence and memory leaks
 * 
 * Phase 1 - Foundation for Lounge UX V2
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import apiClient from "../api/client";
import { toast } from 'sonner';


export function useLoungeSession({
  token,
  activeRoomKey = 'flirty',
  onJoinSuccess,
  onLeaveSuccess,
  onJoinError,
}) {
  const [isInLounge, setIsInLounge] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [loungeStatus, setLoungeStatus] = useState(null);
  const [error, setError] = useState('');
  
  // Track cleanup functions to call on leave
  const cleanupFunctionsRef = useRef([]);
  
  /**
   * Register a cleanup function to be called when leaving lounge
   * Used by components to register their own cleanup (e.g., stop video, clear state)
   */
  const registerCleanup = useCallback((cleanupFn) => {
    cleanupFunctionsRef.current.push(cleanupFn);
    
    // Return unregister function
    return () => {
      cleanupFunctionsRef.current = cleanupFunctionsRef.current.filter(
        fn => fn !== cleanupFn
      );
    };
  }, []);

  /**
   * JOIN LOUNGE - Standardized Entry
   * 
   * Steps:
   * 1. Call backend /api/lounge/join
   * 2. Set lounge status and state
   * 3. Trigger success callback (parent will handle WebRTC, messages, etc.)
   */
  const joinLounge = useCallback(async () => {
    if (isInLounge || isJoining) {
      console.warn('[LoungeSession] Already in lounge or joining in progress');
      return;
    }

    setIsJoining(true);
    setError('');

    try {
      console.log(`[LoungeSession] Joining lounge with room_key: ${activeRoomKey}`);
      
      const response = await apiClient.post(
        `/lounge/join?room_key=${activeRoomKey}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const status = response.data;
      setLoungeStatus(status);
      setIsInLounge(true);
      
      // Store blocked users for filtering (Ticket 15)
      if (status.blocked_users) {
        localStorage.setItem('lounge_blocked_users', JSON.stringify(status.blocked_users));
      }

      // Show subtle entry toast
      toast.success('Entered the Lifestyle Lounge', {
        duration: 2000,
        position: 'top-center',
      });

      console.log('[LoungeSession] Join successful', status);

      // Trigger parent callback for additional setup (WebRTC, load messages, etc.)
      if (onJoinSuccess) {
        await onJoinSuccess(status);
      }

    } catch (error) {
      console.error('[LoungeSession] Failed to join lounge:', error);
      
      const detail = error.response?.data?.detail;
      
      // Handle specific error cases
      if (error.response?.status === 403) {
        // Ticket 20: Check if daily lounge limit reached
        if (detail && typeof detail === 'object' && detail.code === 'LOUNGE_DAILY_LIMIT_REACHED') {
          console.warn('[LoungeSession] Daily limit reached', detail);
          setError('DAILY_LIMIT_REACHED');
        }
        // Ticket 15: Check if it's a lounge ban
        else if (detail && typeof detail === 'object' && detail.code === 'LOUNGE_BLOCKED') {
          const blockedUntil = new Date(detail.blocked_until);
          const formatted = blockedUntil.toLocaleString();
          setError(`You have been temporarily blocked from the Lifestyle Lounge until ${formatted}.`);
        }
        else {
          // No time remaining - show paywall
          console.warn('[LoungeSession] No lounge time remaining');
          setError('OUT_OF_TIME');
        }
      } else {
        setError(error.response?.data?.detail || 'Failed to join lounge');
      }

      // Trigger error callback
      if (onJoinError) {
        onJoinError(error, detail);
      }

    } finally {
      setIsJoining(false);
    }
  }, [token, activeRoomKey, isInLounge, isJoining, onJoinSuccess, onJoinError]);

  /**
   * LEAVE LOUNGE - Comprehensive Cleanup
   * 
   * This is the critical function - ensures EVERYTHING is cleaned up:
   * 1. Call all registered cleanup functions (WebRTC, video, audio, etc.)
   * 2. Call backend /api/lounge/leave
   * 3. Reset local state
   * 4. Show exit confirmation
   */
  const leaveLounge = useCallback(async () => {
    if (!isInLounge) {
      console.warn('[LoungeSession] Not in lounge, nothing to leave');
      return;
    }

    console.log('[LoungeSession] Leaving lounge - starting cleanup');

    try {
      // STEP 1: Execute all registered cleanup functions
      // This includes: stop video, disconnect WebRTC, clear reactions, etc.
      console.log(`[LoungeSession] Running ${cleanupFunctionsRef.current.length} cleanup functions`);
      
      for (const cleanup of cleanupFunctionsRef.current) {
        try {
          await cleanup();
        } catch (err) {
          console.error('[LoungeSession] Cleanup function error:', err);
          // Continue with other cleanups even if one fails
        }
      }

      // STEP 2: Call backend to leave lounge and update time tracking
      const response = await apiClient.post(
        `/lounge/leave`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const status = response.data;
      setLoungeStatus(status);

      // STEP 3: Reset local state
      setIsInLounge(false);
      setError('');

      // Show subtle exit toast
      toast.info('Left the Lounge', {
        duration: 2000,
        position: 'top-center',
      });

      console.log('[LoungeSession] Leave successful', status);

      // Trigger parent callback
      if (onLeaveSuccess) {
        await onLeaveSuccess(status);
      }

    } catch (error) {
      console.error('[LoungeSession] Error leaving lounge:', error);
      
      // Even if backend call fails, still reset local state
      // User shouldn't be stuck "in lounge" on frontend
      setIsInLounge(false);
      setError('');
      
      toast.error('Error leaving lounge, but local state cleared', {
        duration: 2000,
      });
    }
  }, [token, isInLounge, onLeaveSuccess]);

  /**
   * FORCE LEAVE - Emergency cleanup without backend call
   * Used when component unmounts unexpectedly or connection is lost
   */
  const forceLeave = useCallback(() => {
    console.log('[LoungeSession] Force leave - cleanup only, no backend call');
    
    // Run all cleanup functions
    for (const cleanup of cleanupFunctionsRef.current) {
      try {
        cleanup();
      } catch (err) {
        console.error('[LoungeSession] Force leave cleanup error:', err);
      }
    }
    
    // Reset state
    setIsInLounge(false);
    setError('');
  }, []);

  /**
   * AUTO-CLEANUP on component unmount
   * Ensures we never leave ghost presence or open connections
   */
  useEffect(() => {
    return () => {
      if (isInLounge) {
        console.log('[LoungeSession] Component unmounting while in lounge - force cleanup');
        forceLeave();
      }
    };
  }, [isInLounge, forceLeave]);

  return {
    // State
    isInLounge,
    isJoining,
    loungeStatus,
    error,
    
    // Actions
    joinLounge,
    leaveLounge,
    forceLeave,
    registerCleanup,
    
    // Helpers
    clearError: () => setError(''),
  };
}
